import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ChevronLeft, Mail, Trash2, Check, Clock } from 'lucide-react'
import { useAuth } from '../../context/AuthContext'
import { supabase } from '../../lib/supabase'

export default function AdminEnquiries() {
  const { isAuthenticated, isAdmin } = useAuth()
  const navigate = useNavigate()
  const [enquiries, setEnquiries] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [selectedEnquiry, setSelectedEnquiry] = useState(null)

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/account')
      return
    }
    if (!isAdmin) {
      navigate('/')
      return
    }
    fetchEnquiries()
  }, [isAuthenticated, isAdmin])

  const fetchEnquiries = async () => {
    try {
      const { data, error } = await supabase
        .from('contact_enquiries')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) throw error
      setEnquiries(data || [])
    } catch (error) {
      console.error('Error fetching enquiries:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const markAsRead = async (id) => {
    try {
      const { error } = await supabase
        .from('contact_enquiries')
        .update({ read: true })
        .eq('id', id)

      if (error) throw error

      setEnquiries(enquiries.map(e =>
        e.id === id ? { ...e, read: true } : e
      ))
    } catch (error) {
      console.error('Error marking as read:', error)
    }
  }

  const deleteEnquiry = async (id) => {
    if (!confirm('Are you sure you want to delete this enquiry?')) return

    try {
      const { error } = await supabase
        .from('contact_enquiries')
        .delete()
        .eq('id', id)

      if (error) throw error

      setEnquiries(enquiries.filter(e => e.id !== id))
      if (selectedEnquiry?.id === id) {
        setSelectedEnquiry(null)
      }
    } catch (error) {
      console.error('Error deleting enquiry:', error)
    }
  }

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const unreadCount = enquiries.filter(e => !e.read).length

  if (isLoading) {
    return (
      <div className="min-h-screen bg-pp-ivory flex items-center justify-center">
        <div className="animate-pulse text-pp-stone">Loading enquiries...</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-pp-linen">
      <div className="container mx-auto px-4 lg:px-8 py-8">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 text-pp-stone hover:text-pp-charcoal transition-colors mb-6"
        >
          <ChevronLeft className="w-4 h-4" />
          Back to Dashboard
        </Link>

        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-display text-display-md text-pp-ink">
              Contact Enquiries
            </h1>
            <p className="text-pp-stone">
              {unreadCount > 0 ? `${unreadCount} unread message${unreadCount > 1 ? 's' : ''}` : 'All messages read'}
            </p>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Enquiry List */}
          <div className="lg:col-span-1 space-y-2">
            {enquiries.length === 0 ? (
              <div className="bg-white rounded-2xl p-8 border border-pp-sand/50 text-center">
                <Mail className="w-12 h-12 text-pp-sand mx-auto mb-4" />
                <p className="text-pp-stone">No enquiries yet</p>
              </div>
            ) : (
              enquiries.map((enquiry) => (
                <motion.button
                  key={enquiry.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  onClick={() => {
                    setSelectedEnquiry(enquiry)
                    if (!enquiry.read) markAsRead(enquiry.id)
                  }}
                  className={`w-full text-left p-4 rounded-xl border transition-colors ${
                    selectedEnquiry?.id === enquiry.id
                      ? 'bg-pp-sage/10 border-pp-sage'
                      : 'bg-white border-pp-sand/50 hover:border-pp-terracotta/50'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        {!enquiry.read && (
                          <span className="w-2 h-2 bg-pp-terracotta rounded-full flex-shrink-0" />
                        )}
                        <p className={`font-medium text-pp-ink truncate ${!enquiry.read ? 'font-semibold' : ''}`}>
                          {enquiry.name}
                        </p>
                      </div>
                      <p className="text-sm text-pp-stone truncate">{enquiry.subject}</p>
                      <p className="text-xs text-pp-stone/70 mt-1">{formatDate(enquiry.created_at)}</p>
                    </div>
                  </div>
                </motion.button>
              ))
            )}
          </div>

          {/* Enquiry Detail */}
          <div className="lg:col-span-2">
            {selectedEnquiry ? (
              <motion.div
                key={selectedEnquiry.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="bg-white rounded-2xl border border-pp-sand/50 overflow-hidden"
              >
                <div className="p-6 border-b border-pp-sand/50 flex items-center justify-between">
                  <div>
                    <h2 className="font-display text-xl text-pp-ink">{selectedEnquiry.subject}</h2>
                    <p className="text-sm text-pp-stone mt-1">
                      From {selectedEnquiry.name} ({selectedEnquiry.email})
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <a
                      href={`mailto:${selectedEnquiry.email}?subject=Re: ${selectedEnquiry.subject}`}
                      className="p-2 hover:bg-pp-sage/10 rounded-lg transition-colors text-pp-sage"
                      title="Reply"
                    >
                      <Mail className="w-5 h-5" />
                    </a>
                    <button
                      onClick={() => deleteEnquiry(selectedEnquiry.id)}
                      className="p-2 hover:bg-red-50 rounded-lg transition-colors text-red-500"
                      title="Delete"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
                <div className="p-6">
                  <p className="text-xs text-pp-stone mb-4 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {formatDate(selectedEnquiry.created_at)}
                  </p>
                  <div className="prose prose-pp max-w-none">
                    <p className="text-pp-charcoal whitespace-pre-wrap">{selectedEnquiry.message}</p>
                  </div>
                </div>
              </motion.div>
            ) : (
              <div className="bg-white rounded-2xl p-12 border border-pp-sand/50 text-center h-full flex items-center justify-center">
                <div>
                  <Mail className="w-12 h-12 text-pp-sand mx-auto mb-4" />
                  <p className="text-pp-stone">Select an enquiry to view</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
